import { NurseLayout } from "@/components/layout/nurse-layout";
import { Link } from "wouter";
import { ArrowLeft, Award, CheckCircle, BookOpen, Clock, FileText } from "lucide-react";

const REVALIDATION_REQUIREMENTS = [
  { label: "Practice hours", value: "450 hours over 3 years (or 900 for combined nurse/midwife)", icon: Clock },
  { label: "CPD hours", value: "35 hours of CPD — at least 20 must be participatory", icon: BookOpen },
  { label: "Practice-related feedback", value: "5 pieces of practice-related feedback", icon: FileText },
  { label: "Written reflective accounts", value: "5 written reflective accounts (linking to the Code)", icon: FileText },
  { label: "Reflective discussion", value: "1 reflective discussion with an NMC registrant", icon: CheckCircle },
  { label: "Health & character declaration", value: "Self-declaration of good health and character", icon: CheckCircle },
  { label: "Professional indemnity", value: "Evidence of professional indemnity arrangement", icon: CheckCircle },
  { label: "Confirmation", value: "Confirmation from a confirmer (line manager or NMC registrant)", icon: Award },
];

const NMC_CODE_PILLARS = [
  {
    title: "Prioritise people",
    colour: "bg-teal-50 border-teal-200 text-teal-800",
    points: [
      "Treat people as individuals and uphold their dignity",
      "Listen to people and respond to their preferences and concerns",
      "Make sure people's physical, social and psychological needs are assessed and responded to",
      "Act in the best interests of people at all times",
      "Respect and uphold people's human rights",
    ],
  },
  {
    title: "Practise effectively",
    colour: "bg-blue-50 border-blue-200 text-blue-800",
    points: [
      "Always practise in line with the best available evidence",
      "Communicate clearly and maintain accurate records",
      "Work cooperatively with colleagues",
      "Share your skills, knowledge and experience",
      "Keep your knowledge and skills up to date",
    ],
  },
  {
    title: "Preserve safety",
    colour: "bg-amber-50 border-amber-200 text-amber-800",
    points: [
      "Recognise and work within the limits of your competence",
      "Be open and candid with all service users",
      "Act without delay if you believe patient safety is at risk",
      "Raise and act on concerns about risk, poor practice or wrongdoing",
      "Advise on, prescribe, supply, dispense or administer medicines within your competence",
    ],
  },
  {
    title: "Promote professionalism and trust",
    colour: "bg-purple-50 border-purple-200 text-purple-800",
    points: [
      "Uphold the reputation of your profession at all times",
      "Cooperate with all investigations and audits",
      "Respond appropriately to any complaints made about you",
      "Provide leadership in ensuring the evidence base of practice",
      "Comply with relevant laws and recognise boundaries of your own competence",
    ],
  },
];

const RHEUM_REFLECTION_PROMPTS = [
  "Reflect on a complex biologic monitoring conversation with a patient and how you managed uncertainty.",
  "Describe a time you escalated a red flag concern (e.g. abnormal LFTs on MTX) and the outcome.",
  "Reflect on how you keep your DMARD monitoring knowledge up to date and how this impacts your practice.",
  "Describe an experience educating a patient starting their first biologic — what went well and what you would change.",
  "Reflect on working in the MDT — how has collaboration shaped your nursing practice in rheumatology?",
];

export default function NmcStandards() {
  return (
    <NurseLayout title="NMC Standards & Revalidation">
      <Link href="/nurse/career">
        <button className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Career Hub
        </button>
      </Link>

      <div className="space-y-8">

        {/* Header */}
        <div className="bg-gradient-to-br from-indigo-900 to-indigo-950 rounded-2xl p-8 text-white">
          <div className="flex items-center gap-4 mb-3">
            <div className="bg-indigo-800/60 p-3 rounded-xl">
              <Award className="h-7 w-7 text-indigo-300" />
            </div>
            <div>
              <h2 className="text-xl font-bold">NMC Standards & Revalidation</h2>
              <p className="text-indigo-300 text-sm">The NMC Code · 3-Year Revalidation · PREP</p>
            </div>
          </div>
          <p className="text-indigo-200 text-sm leading-relaxed max-w-3xl">
            All NMC registrants (nurses and midwives) must revalidate every three years to remain on the register. Revalidation demonstrates that you are practising safely and effectively and are living the values of the NMC Code.
          </p>
        </div>

        {/* Revalidation requirements */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Three-Year Revalidation Requirements</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {REVALIDATION_REQUIREMENTS.map((req, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-3">
                <div className="bg-indigo-100 p-2 rounded-lg shrink-0">
                  <req.icon className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-slate-800">{req.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{req.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* NMC Code */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-2">The NMC Code (2015)</h3>
          <p className="text-sm text-slate-500 mb-4">The four pillars of the NMC Code — professional standards for nurses, midwives, and nursing associates registered with the NMC.</p>
          <div className="grid gap-4 md:grid-cols-2">
            {NMC_CODE_PILLARS.map((pillar) => (
              <div key={pillar.title} className={`rounded-xl border p-5 ${pillar.colour}`}>
                <h4 className="font-bold text-sm mb-3">{pillar.title}</h4>
                <ul className="space-y-1.5">
                  {pillar.points.map((pt, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs leading-relaxed">
                      <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 opacity-70" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Rheumatology-specific reflection prompts */}
        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
          <h3 className="font-bold text-teal-900 mb-3 flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-700" />
            Rheumatology Reflection Prompts
          </h3>
          <p className="text-sm text-teal-700 mb-4">Use these prompts to write your five NMC reflective accounts — each must reference a specific part of the Code.</p>
          <ul className="space-y-3">
            {RHEUM_REFLECTION_PROMPTS.map((prompt, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-teal-800">
                <span className="h-5 w-5 rounded-full bg-teal-200 text-teal-800 text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                {prompt}
              </li>
            ))}
          </ul>
        </div>

        {/* Links */}
        <div className="flex flex-wrap gap-3">
          <a href="https://www.nmc.org.uk/revalidation/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-indigo-700 text-white px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-indigo-800 transition-colors">
            <Award className="h-4 w-4" /> NMC Revalidation Portal
          </a>
          <a href="https://www.nmc.org.uk/standards/code/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2.5 rounded-xl text-sm font-medium hover:border-indigo-300 hover:text-indigo-700 transition-colors">
            <BookOpen className="h-4 w-4" /> The NMC Code (full text)
          </a>
        </div>
      </div>
    </NurseLayout>
  );
}
